// ====================================================================
// THE LOST FENNEC — the Grove Keeper's critter-rescue quest in the oasis
// ====================================================================
// The game's second quest, and the reason to wander the new Grove zone
// (src/world/zones/grove.js): the Grove Keeper stands by the oasis pond and
// her little fennec fox, Dot, has scampered off into the dunes. Talk to the
// Keeper (the same bump-zone speech bubble everything else uses) and a soft
// "look here!" marker appears out over Dot's hiding spot by the western rocks.
// Finding Dot is EXPLORING, not jumping: the per-frame update samples the
// player's position, and simply walking within reach of Dot completes the
// rescue — Dot scampers home to the Keeper, and Chocky earns the Explorer
// Pith Helmet, the quest-only item in the store catalog.
//
// State ('searching' | 'done') persists via game/save.js, so a rescued Dot
// stays home across reloads.
//
// Built through a ctx from main.js (the zone/store/quest pattern): world
// builders, bump zones, FX, and the wallet stay main.js-owned; this module
// owns the Keeper, Dot, the marker, and the quest state machine.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW } from '../core/tier.js';
import { loadSave, patchSave } from './save.js';
import { equip, getEquipped } from './outfits.js';
import { heightAt } from '../world/heightfield.js';
import { spawnActor, despawnActor } from '../world/actors.js';
import { markDynamic } from '../world/freezeStatics.js';

const QUEST_ID = 'critter';
const KEEPER = { x: -44, z: 2.5 };
const PET_HIDE = { x: -51, z: -6.5 };   // out by the western rocks
const PET_HOME = { x: -45.6, z: 2.6 };  // beside the Keeper once rescued
const FIND_RADIUS = 1.4;
// The Keeper faces back toward the zone road (the +x direction, village side).
const GROVE_FACE = { x: -38, z: 0 };

let state = null;                       // null | 'searching' | 'done'
function setState(s) {
  state = s;
  patchSave({ quests: { ...loadSave().quests, [QUEST_ID]: s } });
}

// ---- mesh helpers (the wordville/quest trick: vertex-coloured primitives
// merged into ONE geometry per articulated part, one cheap Lambert material) --
function tinted(geo, hex) {
  const c = new THREE.Color(hex);
  const n = geo.attributes.position.count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b; }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}
function coloredBox(w, h, d, x, y, z, hex) {
  const g = new THREE.BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return tinted(g, hex);
}

// ---- The Grove Keeper: a box-built explorer in a khaki kit and a wide safari
// hat, with a watering can at her side. Two draw calls: body + head (the head
// turns to watch you, like Farmer Pip).
function buildKeeper(ctx) {
  const KHAKI = 0xb7a06a, SHIRT = 0x4a8a6a, SKIN = 0xe8c49a;
  const HAT = 0xd8c596, HAT_DK = 0xb7a06a, METAL = 0x8a9aa0;

  const can = tinted(new THREE.CylinderGeometry(0.1, 0.11, 0.2, 8), METAL);
  can.translate(0.34, 0.18, 0.04);
  const spout = tinted(new THREE.CylinderGeometry(0.025, 0.025, 0.22, 6), METAL);
  spout.rotateZ(-0.7);
  spout.translate(0.48, 0.24, 0.04);

  const bodyGeo = mergeGeometries([
    coloredBox(0.15, 0.3, 0.16,   0.11, 0.15, 0,    KHAKI),    // legs
    coloredBox(0.15, 0.3, 0.16,  -0.11, 0.15, 0,    KHAKI),
    coloredBox(0.17, 0.09, 0.2,   0.11, 0.045, 0.02, 0x5a4632), // boots
    coloredBox(0.17, 0.09, 0.2,  -0.11, 0.045, 0.02, 0x5a4632),
    coloredBox(0.44, 0.46, 0.28,  0,    0.6, 0,     SHIRT),    // torso
    coloredBox(0.5, 0.1, 0.3,     0,    0.86, 0,    KHAKI),    // collar
    coloredBox(0.13, 0.42, 0.13,  0.32, 0.68, 0,    SHIRT),    // arms
    coloredBox(0.13, 0.42, 0.13, -0.32, 0.68, 0,    SHIRT),
    coloredBox(0.11, 0.1, 0.11,   0.32, 0.44, 0,    SKIN),     // hands
    coloredBox(0.11, 0.1, 0.11,  -0.32, 0.44, 0,    SKIN),
    can, spout,
  ], false);

  const hatBrim = tinted(new THREE.CylinderGeometry(0.38, 0.41, 0.04, 14), HAT);
  hatBrim.translate(0, 0.34, 0);
  const hatDome = tinted(new THREE.SphereGeometry(0.21, 10, 7, 0, Math.PI * 2, 0, Math.PI / 2), HAT_DK);
  hatDome.translate(0, 0.35, 0);
  const headGeo = mergeGeometries([
    coloredBox(0.32, 0.32, 0.32,  0, 0.16, 0,    SKIN),        // head
    coloredBox(0.06, 0.06, 0.06,  0, 0.13, 0.18, 0xdfae84),    // nose
    hatBrim, hatDome,
  ], false);

  const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
  const group = new THREE.Group();
  const body = new THREE.Mesh(bodyGeo, mat);
  body.castShadow = !LOW;
  group.add(body);
  const head = new THREE.Mesh(headGeo, mat);
  head.position.y = 1.0;
  group.add(head);

  group.position.set(KEEPER.x, heightAt(KEEPER.x, KEEPER.z), KEEPER.z);
  markDynamic(group);   // update() bobs and turns the keeper
  ctx.propsGroup.add(group);
  return { group, head };
}

// ---- Dot the fennec: a tiny sandy fox with big cream ears and a bushy
// tail. Two draw calls: body+legs+tail (static) and head+ears+muzzle (the
// head group wiggles).
function buildFennec(ctx) {
  const SAND = 0xe6c58a, SAND_DK = 0xcaa86a, CREAM = 0xf4e8cf, NOSE = 0x4a3a2c;

  const bodyGeo = mergeGeometries([
    coloredBox(0.34, 0.2, 0.2,   0,     0.2, 0,    SAND),       // torso
    coloredBox(0.06, 0.16, 0.06,  0.12, 0.08, 0.07, SAND_DK),   // legs
    coloredBox(0.06, 0.16, 0.06,  0.12, 0.08, -0.07, SAND_DK),
    coloredBox(0.06, 0.16, 0.06, -0.12, 0.08, 0.07, SAND_DK),
    coloredBox(0.06, 0.16, 0.06, -0.12, 0.08, -0.07, SAND_DK),
  ], false);
  // Bushy tail with a cream tip, angled up.
  const tail = tinted(new THREE.CylinderGeometry(0.05, 0.1, 0.26, 6), SAND);
  tail.rotateX(0.8);
  tail.translate(-0.2, 0.26, 0);
  const tailTip = tinted(new THREE.SphereGeometry(0.06, 6, 5), CREAM);
  tailTip.translate(-0.28, 0.4, 0);
  const bodyMerged = mergeGeometries([bodyGeo, tail, tailTip], false);

  const headGeo = mergeGeometries([
    coloredBox(0.2, 0.18, 0.18,  0,     0,     0,    SAND),     // head
    coloredBox(0.1, 0.08, 0.1,   0.0,  -0.04,  0.12, CREAM),    // muzzle
    coloredBox(0.04, 0.04, 0.04, 0.0,  -0.02,  0.18, NOSE),     // nose
    // Big triangular ears.
    tinted((() => { const g = new THREE.ConeGeometry(0.07, 0.22, 4); g.translate(0.07, 0.18, 0); return g; })(), CREAM),
    tinted((() => { const g = new THREE.ConeGeometry(0.07, 0.22, 4); g.translate(-0.07, 0.18, 0); return g; })(), CREAM),
  ], false);

  const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
  const group = new THREE.Group();
  const body = new THREE.Mesh(bodyMerged, mat);
  body.castShadow = !LOW;
  group.add(body);
  const head = new THREE.Group();
  head.position.set(0.2, 0.26, 0);
  head.add(new THREE.Mesh(headGeo, mat));
  group.add(head);

  markDynamic(group);   // Dot bobs, wiggles her ears, and moves home when rescued
  ctx.propsGroup.add(group);
  return { group, head };
}

export function initQuest(ctx) {
  state = loadSave().quests[QUEST_ID] || null;

  const keeper = buildKeeper(ctx);
  const keeperBaseY = keeper.group.position.y;
  const REST_YAW = Math.atan2(KEEPER.x - GROVE_FACE.x, KEEPER.z - GROVE_FACE.z);
  let yaw = REST_YAW;
  keeper.group.rotation.y = yaw;
  keeper.actor = spawnActor('grove-keeper', keeper);

  const fennec = buildFennec(ctx);
  // Dot starts hidden by the rocks; if she's already been rescued, she's home.
  const petAt = state === 'done' ? PET_HOME : PET_HIDE;
  fennec.group.position.set(petAt.x, heightAt(petAt.x, petAt.z), petAt.z);
  fennec.group.rotation.y = Math.random() * Math.PI * 2;
  fennec.actor = spawnActor('fennec', fennec);

  // A soft "look here!" marker bobbing over Dot while she's lost — a gentle
  // nudge for explorers, not a waypoint line. Hidden unless the quest is active.
  const marker = new THREE.Group();
  const ring = new THREE.Mesh(
    new THREE.ConeGeometry(0.16, 0.34, 4),
    new THREE.MeshBasicMaterial({ color: 0xffd23f })
  );
  ring.rotation.x = Math.PI;                       // point down at Dot
  marker.add(ring);
  marker.position.set(PET_HIDE.x, heightAt(PET_HIDE.x, PET_HIDE.z) + 1.1, PET_HIDE.z);
  markDynamic(marker);   // bobs/spins while Dot is lost
  ctx.propsGroup.add(marker);
  function refreshMarker() { marker.visible = state === 'searching'; }
  refreshMarker();

  // ---- Talking to the Keeper: sticky speech bubble while you stand with her.
  // No accept button — saying hello starts the search (kid-proof).
  ctx.addBumpZone(KEEPER.x, KEEPER.z, 1.7, 'grove-keeper', (sourceId) => {
    if (!state) {
      setState('searching');
      refreshMarker();
      ctx.showBumpMsg('🧭 GROVE KEEPER', "Oh Chocky, my little fennec Dot ran off into the dunes! Look for her by the big rocks to the west — follow the gold arrow!", 0, sourceId);
    } else if (state === 'searching') {
      ctx.showBumpMsg('🧭 GROVE KEEPER', 'Dot is still out there by the western rocks. Go on — see that gold arrow? She\'s right under it!', 0, sourceId);
    } else {
      ctx.showBumpMsg('🧭 GROVE KEEPER', "You brought Dot home safe! That explorer's pith helmet suits a finder like you 🦊", 0, sourceId);
    }
  }, (sourceId) => ctx.hideBumpMsg(sourceId));

  function completeRescue() {
    setState('done');
    refreshMarker();
    // Dot scampers home to the Keeper.
    fennec.group.position.set(PET_HOME.x, heightAt(PET_HOME.x, PET_HOME.z), PET_HOME.z);
    ctx.spawnDust(PET_HIDE.x, heightAt(PET_HIDE.x, PET_HIDE.z) + 0.1, PET_HIDE.z, 14, 1.4);
    ctx.addCamShake(0.18);
    ctx.triggerFace('grin', 1.5);
    // The wardrobe reward: own the Explorer Pith Helmet, wear it if bare-headed
    // (never knock off a hat the kid paid for).
    const owned = new Set(loadSave().owned);
    owned.add('pith');
    patchSave({ owned: [...owned] });
    if (!getEquipped().head) equip('head', 'pith');
    ctx.showBumpMsg('🦊 FOUND DOT!', "You found the Grove Keeper's fennec! The EXPLORER PITH HELMET is yours — it's waiting in Chocky's Closet.", 8, 'fennec');
  }

  // Per-frame (play mode): the Keeper breathes and turns to watch the player;
  // Dot wiggles her ears and (while lost) the marker bobs; finding her is just
  // walking within reach. No allocation in here.
  function update(dt, t) {
    const p = ctx.getPlayerPos();
    const kdx = p.x - KEEPER.x, kdz = p.z - KEEPER.z;
    const near = kdx * kdx + kdz * kdz < 2025;     // asleep past ~45 units
    if (!near) return;

    keeper.group.position.y = keeperBaseY + Math.sin(t * 2.2) * 0.015;
    const want = (kdx * kdx + kdz * kdz < 49) ? Math.atan2(kdx, kdz) : REST_YAW;
    let diff = want - yaw;
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;
    yaw += diff * Math.min(1, dt * 3);
    keeper.group.rotation.y = yaw;

    // Dot: ear wiggle + a little bob; turns toward Chocky when he's close.
    fennec.head.rotation.z = Math.sin(t * 4) * 0.12;
    fennec.group.position.y = heightAt(fennec.group.position.x, fennec.group.position.z) + Math.abs(Math.sin(t * 3)) * 0.04;

    if (state === 'searching') {
      marker.position.y = heightAt(PET_HIDE.x, PET_HIDE.z) + 1.05 + Math.sin(t * 3) * 0.12;
      marker.rotation.y += dt * 1.5;
      const fdx = p.x - PET_HIDE.x, fdz = p.z - PET_HIDE.z;
      if (fdx * fdx + fdz * fdz < FIND_RADIUS * FIND_RADIUS) completeRescue();
    }
  }

  function dispose() {
    despawnActor(keeper.actor);
    despawnActor(fennec.actor);
  }

  // Wayfinding beacon for the minimap compass (src/game/minimap.js): while Dot
  // is lost, point at her hiding spot; otherwise nothing.
  function getBeacon() {
    return state === 'searching' ? { x: PET_HIDE.x, z: PET_HIDE.z, label: 'Find Dot' } : null;
  }

  return { update, getBeacon, dispose };
}
